import {
  ClusterStatusChip,
  ClusterStatusTooltip,
} from '@cast/features-cluster-ui';
import { useCallback, useMemo, useState } from 'react';

import dayjs from 'dayjs';

import { DATE_TIME_NO_SEC_AMPM } from '@cast/constants';
import {
  Column,
  Icons,
  TableActionButton,
  TableBulkActions,
  TableHeader,
  LinkBehaviour,
  makeActionsColumn,
  NoData,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';
import { Cluster, ClusterDisplayStatus } from '@cast/types';

import { ClusterActionsMenu } from 'components/cluster';
import { FeaturesCell } from 'components/cluster/FeaturesCell';
import { Link } from 'components/router';
import { tableCacheKeys } from 'constants/tableCacheKeys';
import { useAbility } from '@cast/shared-ability-data-access';
import { useNavigate } from '@cast/infrastructure-organization';

import { NotFoundClusterItem, toClusterListEntity } from './types';
import { useRemoveNotFoundClustersDialog } from './useRemoveNotFoundClustersDialog';

import {
  NoResults,
  SearchableTable,
  SearchableTableRecordsCount,
} from '@cast/shared-search-ui';

export const NotFoundClustersTable = () => {
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const navigate = useNavigate();
  const ability = useAbility();
  const canDeleteSelectedClusters = useMemo(
    () =>
      selectedRows.length > 0 &&
      selectedRows.every((clusterId) =>
        ability.can('delete', 'Clusters', { clusterId })
      ),
    [ability, selectedRows]
  );
  const { openDialog: openRemoveNotFoundClustersDialog } =
    useRemoveNotFoundClustersDialog();

  const handleDeleteSelected = useCallback(() => {
    openRemoveNotFoundClustersDialog({
      clusterIds: selectedRows,
      onSuccess: () => setSelectedRows([]),
    });
  }, [openRemoveNotFoundClustersDialog, selectedRows]);

  return (
    <SearchableTable<NotFoundClusterItem, string>
      cacheKey={tableCacheKeys.NOT_FOUND_CLUSTERS}
      testId="not-found-clusters-table"
      getRowTestId={({ id }) => `cluster-row-${id}`}
      fillParentHeight
      sizing="m"
      rowKey="id"
      selectedRows={selectedRows}
      onRowsSelected={setSelectedRows}
      onRowClick={(event, { row }) => {
        if (!window.getSelection()?.toString()) {
          navigate(`/external-clusters/${row.id}`, { clickEvent: event });
        }
      }}
      checkboxColumnProps={{ cellWrapperSx: { pl: 32, pr: 2 }, width: 50 }}
      components={{
        noResults: <NoResults entity="cluster" />,
        noData: <NoData icon={<Icons.Nut />} entity="cluster" />,
      }}
      sx={{
        mx: -32,
        '& .DS-Table-RowWrapper.DS-Table-HeaderRowWrapper': {
          borderTop: 'none',
        },
      }}
      outerHeader={
        <TableHeader
          recordsCount={
            <SearchableTableRecordsCount
              title="cluster"
              postfix="not found"
              showZero
            />
          }
          bulkActions={
            <TableBulkActions visible={!!selectedRows.length}>
              <TableActionButton
                testId="delete-selected-not-found-clusters"
                startIcon={<Icons.Trash />}
                disruptive
                disabled={!canDeleteSelectedClusters}
                onClick={handleDeleteSelected}
              >
                Delete selected ({selectedRows.length})
              </TableActionButton>
            </TableBulkActions>
          }
          showSettings={false}
          px={32}
        />
      }
    >
      <Column<NotFoundClusterItem>
        id="name"
        header="Cluster name"
        minWidth={200}
        sortable
        cellWrapperSx={{ px: 16 }}
        renderCell={(cluster) => (
          <Stack minWidth={0}>
            <LinkBehaviour
              component={Link}
              variant="P12M"
              noWrap
              sx={{ textDecoration: 'none' }}
              to={`/external-clusters/${cluster.id}`}
              onClick={(e) => e.stopPropagation()}
              testId="name-link"
            >
              <Typography component="span" variant="inherit" data-testid="name">
                {cluster.name}
              </Typography>
            </LinkBehaviour>
            {cluster.providerNamespaceId && (
              <Typography
                variant="P10R"
                color={tokens.theme.fg.structural.secondary}
                noWrap
              >
                {cluster.providerNamespaceId}
              </Typography>
            )}
          </Stack>
        )}
        accessor="name"
        resizable
      />
      <Column<NotFoundClusterItem>
        id="features"
        header="Features"
        width={140}
        renderCell={(cluster) => (
          <FeaturesCell {...toClusterListEntity(cluster)} />
        )}
      />
      <Column<NotFoundClusterItem>
        id="lastConnection"
        header="Last connection"
        minWidth={170}
        sortable
        renderCell={({ agentSnapshotReceivedAt }) =>
          agentSnapshotReceivedAt ? (
            <Typography variant="P12R" color="grey.900">
              {dayjs(agentSnapshotReceivedAt).format(DATE_TIME_NO_SEC_AMPM)}
            </Typography>
          ) : (
            <Typography
              variant="P12R"
              color={tokens.theme.fg.structural.secondary}
            >
              –
            </Typography>
          )
        }
        accessor={({ agentSnapshotReceivedAt }) =>
          agentSnapshotReceivedAt ? dayjs(agentSnapshotReceivedAt).unix() : 0
        }
      />
      <Column<NotFoundClusterItem>
        id="status"
        header="Status"
        width={120}
        renderCell={(cluster) => (
          <ClusterStatusTooltip
            cluster={(cluster.original ?? cluster) as Cluster}
          >
            <ClusterStatusChip
              status={ClusterDisplayStatus.NOT_FOUND}
              managed={cluster.isManaged}
              size="small"
              noCaption
            />
          </ClusterStatusTooltip>
        )}
      />
      {makeActionsColumn<NotFoundClusterItem>({
        width: 62,
        cellWrapperSx: { pl: 8, pr: 16 },
        headerCellWrapperSx: { width: 0, minWidth: 0, padding: 0 },
        action: (cluster) => (
          <ClusterActionsMenu cluster={toClusterListEntity(cluster).original} />
        ),
      })}
    </SearchableTable>
  );
};
